import "./CardOpeningHours.scss";
import clsx from "clsx";
import { OpeningHours } from "@anoki/fse-common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Col, Row } from "@/components";
import { HeadingLevel } from "@/types/headings";

type WeekDaysMap = Record<keyof OpeningHours, string>;
type TimeSlot = { from: string; to: string };

type CardOpeningHoursProps = {
  title: string;
  titleTag: HeadingLevel;
  openingHours: OpeningHours;
  weekDaysMap: WeekDaysMap;
  openLabel?: string;
  todayLabel?: string;
  closeLabel?: string;
  open24Label?: string;
  opensSoonLabel?: string;
  opensAtLabel?: string;
  opensTomorrowLabel?: string;
  opensOnLabel?: string;
  atLabel?: string;
  closesAtLabel?: string;
};

export const CardOpeningHours = ({
  title = "Orari di apertura",
  titleTag: TitleTag = "h1",
  openingHours,
  weekDaysMap,
  openLabel = "Aperto",
  todayLabel = "Oggi",
  closeLabel = "Chiuso",
  open24Label = "24 ore su 24",
  opensSoonLabel = "Apre tra poco",
  opensAtLabel = "Apre alle",
  opensTomorrowLabel = "Apre domani",
  opensOnLabel = "Apre",
  atLabel = "alle",
  closesAtLabel = "Chiude alle",
}: CardOpeningHoursProps) => {
  const [activeDay, setActiveDay] = useState<number | null>(null);
  const [dayLabel, setDayLabel] = useState("");
  const [openingHoursText, setOpeningHoursText] = useState("");
  const [openingHoursText2, setOpeningHoursText2] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isOpeningSoon, setIsOpeningSoon] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");

  const weekDays = Object.values(weekDaysMap);
  const weekKeys = Object.keys(weekDaysMap) as (keyof OpeningHours)[];

  const formatCurrentTime = () => {
    return DateTime.now().toFormat("HH:mm");
  };

  const parseTimeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const isSlot24Hours = useCallback(
    (slot: TimeSlot) => slot.from === "00:00" && slot.to === "00:00",
    [],
  );

  const is24HoursDay = useCallback(
    (slots: TimeSlot[]) => slots.some(isSlot24Hours),
    [isSlot24Hours],
  );

  const isCurrentlyOpen = (slots: TimeSlot[], currentTime: string) => {
    for (const { from, to } of slots) {
      if (currentTime >= from && currentTime <= to) {
        return { open: true, closingTime: to };
      }
    }
    return { open: false, closingTime: "" };
  };

  const getNextOpeningTime = (slots: TimeSlot[], currentTime: string) => {
    for (const { from } of slots) {
      if (from > currentTime) {
        return from;
      }
    }
    return "";
  };

  const checkIsOpeningSoon = useCallback(
    (openingTime: string, currentTime: string) => {
      if (!openingTime) return false;

      const openMinutes = parseTimeToMinutes(openingTime);
      const currentMinutes = parseTimeToMinutes(currentTime);
      const diffMinutes = openMinutes - currentMinutes;

      return diffMinutes > 0 && diffMinutes <= 60;
    },
    [],
  );

  const getNextOpenDay = useCallback(
    (currentDayIndex: number) => {
      for (let i = 1; i <= 7; i++) {
        const nextIndex = (currentDayIndex + i) % 7;
        const nextDayKey = weekKeys[nextIndex];
        const nextDaySlots = openingHours[nextDayKey];

        if (nextDaySlots && nextDaySlots.length > 0) {
          return {
            dayName: weekDays[nextIndex].toLowerCase(),
            isTomorrow: i === 1,
          };
        }
      }
      return null;
    },
    [weekKeys, openingHours, weekDays],
  );

  const generateOpeningHoursText = useMemo(() => {
    return (hours: TimeSlot[]) => {
      if (hours.length === 0) {
        return closeLabel;
      }

      if (is24HoursDay(hours)) {
        return `${openLabel} ${open24Label}`;
      }

      return hours.map(({ from, to }) => `${from} - ${to}`).join(" | ");
    };
  }, [closeLabel, is24HoursDay, open24Label, openLabel]);

  useEffect(() => {
    const now = DateTime.now();
    const todayIndex = now.weekday - 1;
    setActiveDay(todayIndex);
    setDayLabel(weekDays[todayIndex] || "");

    const todayKey = weekKeys[todayIndex];
    const todaySlots = openingHours[todayKey];

    if (!todaySlots) {
      return;
    }

    const currentTime = formatCurrentTime();

    if (is24HoursDay(todaySlots)) {
      setOpeningHoursText(openLabel);
      setAdditionalInfo(` | ${open24Label}`);
      setIsOpen(true);
      setIsOpeningSoon(false);
      setOpeningHoursText2(`${openLabel} ${open24Label}`);
      return;
    }

    const { open, closingTime } = isCurrentlyOpen(todaySlots, currentTime);

    if (open) {
      setOpeningHoursText(openLabel);
      setAdditionalInfo(
        closingTime ? ` | ${closesAtLabel} ${closingTime}` : "",
      );
      setIsOpen(true);
      setIsOpeningSoon(false);
    } else {
      const nextOpeningTime = getNextOpeningTime(todaySlots, currentTime);
      setIsOpen(false);

      if (nextOpeningTime) {
        if (checkIsOpeningSoon(nextOpeningTime, currentTime)) {
          setOpeningHoursText(opensSoonLabel);
          setAdditionalInfo(` | ${atLabel} ${nextOpeningTime}`);
          setIsOpeningSoon(true);
        } else {
          setOpeningHoursText(closeLabel);
          setAdditionalInfo(` | ${opensAtLabel} ${nextOpeningTime}`);
          setIsOpeningSoon(false);
        }
      } else {
        setOpeningHoursText(closeLabel);
        setIsOpeningSoon(false);
        const nextDay = getNextOpenDay(todayIndex);

        if (nextDay) {
          setAdditionalInfo(
            nextDay.isTomorrow
              ? ` | ${opensTomorrowLabel}`
              : ` | ${opensOnLabel} ${nextDay.dayName}`,
          );
        } else {
          setAdditionalInfo("");
        }
      }
    }

    setOpeningHoursText2(generateOpeningHoursText(todaySlots));
  }, [
    atLabel,
    checkIsOpeningSoon,
    closeLabel,
    closesAtLabel,
    generateOpeningHoursText,
    getNextOpenDay,
    is24HoursDay,
    open24Label,
    openLabel,
    openingHours,
    opensAtLabel,
    opensOnLabel,
    opensSoonLabel,
    opensTomorrowLabel,
    weekDays,
    weekKeys,
  ]);

  return (
    <Col
      fs={"6"}
      fw={"semibold"}
      className="ui-card-opening rounded shadow text-slate-28"
    >
      <Col as={"header"} className="ui-card-header">
        <TitleTag className="text-slate-20 fw-semibold fs-4">{title}</TitleTag>
        <Row
          justifyContent={"space-between"}
          alignItems={"center"}
          style={{ backgroundColor: "#F7F2FC" }}
          py={"x16"}
          px={"x24"}
          fw={"bold"}
          fs={"6"}
          className="rounded"
        >
          <p className="text-slate-28">
            {todayLabel}, {dayLabel}
          </p>
          <p>
            <span
              className={
                isOpen
                  ? "text-success"
                  : isOpeningSoon
                    ? "text-warning"
                    : "text-error"
              }
            >
              {openingHoursText}
            </span>
            <span className="fw-regular text-grey-black">{additionalInfo}</span>
          </p>
        </Row>
      </Col>
      <Col as={"main"} pt={"x32"} gap={"x24"}>
        <Row justifyContent={"space-around"} className="ui-weekdays-container">
          {weekDays.map((day, index) => (
            <Row
              justifyContent={"center"}
              alignItems={"center"}
              key={index}
              className={clsx(
                activeDay === index ? "active-day text-primary" : "",
                "flex-1",
              )}
            >
              {day.charAt(0).toUpperCase()}
            </Row>
          ))}
        </Row>
        <Row
          justifyContent={"space-between"}
          alignItems={"center"}
          fw={"regular"}
        >
          <p>{dayLabel}</p>
          <p>{openingHoursText2}</p>
        </Row>
      </Col>
    </Col>
  );
};
